<script lang="ts">
  import * as yup from 'yup';
  import Button from 'Catalyst/components/Button/Button.svelte';
  import Form from 'Components/Common/FormManager/Form.svelte';
  import FormControl from 'Catalyst/components/FormControl/FormControl.svelte';
  import Header from 'Components/SessionV2/Header.svelte';
  import IconButton from 'Catalyst/components/IconButton/IconButton.svelte';
  import Switcher from 'Components/SessionV2/Switcher.svelte';
  import TextInput from 'Catalyst/components/TextInput/TextInput.svelte';
  import {validateEmail} from 'Utility/validation';
  import {writable, type Writable} from 'svelte/store';
  import type {User} from 'Utility/users/signUpStore';
  import {registerUser} from 'Utility/users/signUpStore';

  export let authenticityToken: string;
  export let signUpPath: string;
  export let companyPrimaryColor: string;
  export let toggleSignIn: () => void;

  let isSubmitting = false;

  let revealPassword = false;
  let revealConfirmPassword = false;
  let isValid: boolean = false;
  let errorList: Writable<Object> = writable({});

  const user: User = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    profile: {
      firstName: '',
      lastName: '',
    },
  };

  const onSubmit = async (): Promise<void> => {
    isSubmitting = true;
    try {
      await registerUser(authenticityToken, signUpPath, {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        password: user.password,
        passwordConfirmation: user.passwordConfirmation,
        profile: {
          firstName: user.firstName,
          lastName: user.lastName,
        },
      });
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      isSubmitting = false;
    }
  };

  const validationSchema = yup.object().shape({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    email: yup
      .string()
      .test('email', 'Please provide a valid email', email =>
        validateEmail(email)
      )
      .required('Email is required'),
    password: yup
      .string()
      .test(
        'password',
        'Password must have between 10 and 64 characters, and contain letters, numbers or special characters',
        password => {
          const validPassword = /^[\S]{10,64}$/;
          return validPassword.test(password);
        }
      )
      .required('Password is required'),
    confirmPassword: yup
      .string()
      .test('confirmPassword', (confirmPassword, {createError, parent}) => {
        if (parent.password !== confirmPassword) {
          return createError({message: 'Passwords must match'});
        }
        return true;
      })
      .required('Confirm password is required'),
  });

  $: disableSubmit = isSubmitting || !isValid;
</script>

<Header text="Create your account" />

<Form
  id="sign-up"
  class="w-full flex flex-col mb-0 gap-[1.5rem]"
  on:submit={onSubmit}
  schema={validationSchema}
  bind:errorList
  bind:valid={isValid}
  validateOnBlur
>
  <input
    type="hidden"
    name="authenticity_token"
    value={authenticityToken}
    data-testid="authenticity-token-input"
  />

  <div>
    <div class="flex flex-col gap-[1rem]">
      <FormControl forName="firstName">
        <TextInput
          bind:value={user.firstName}
          id="first_name"
          name="firstName"
          label="First Name"
          invalid={$errorList['firstName']}
        />
      </FormControl>

      <FormControl forName="lastName">
        <TextInput
          bind:value={user.lastName}
          id="last_name"
          name="lastName"
          label="Last Name"
          invalid={$errorList['lastName']}
        />
      </FormControl>

      <FormControl forName="email">
        <TextInput
          bind:value={user.email}
          id="email"
          name="email"
          label="Email"
          invalid={$errorList['email']}
        />
      </FormControl>

      <FormControl forName="password">
        <TextInput
          bind:value={user.password}
          id="password"
          name="password"
          type={!revealPassword ? 'password' : 'text'}
          label="Password"
          invalid={$errorList['password']}
          autocomplete="off"
        >
          <IconButton
            data-testid="password-visibility-button"
            iconFilled={revealPassword}
            slot="suffix"
            icon={revealPassword ? 'visibility' : 'visibility_off'}
            class="mr-[0.375rem] w-[1.75rem] h-[1.75rem] focus-visible:shadow-none focus-visible:bg-[--icon-button-variants-style-standard-surface-hover]"
            on:click={() => {
              revealPassword = !revealPassword;
            }}
          />
        </TextInput>
      </FormControl>

      <FormControl forName="confirmPassword">
        <TextInput
          bind:value={user.passwordConfirmation}
          id="confirm_password"
          name="confirmPassword"
          type={!revealConfirmPassword ? 'password' : 'text'}
          label="Confirm Password"
          invalid={$errorList['confirmPassword']}
          autocomplete="off"
        >
          <IconButton
            data-testid="password-visibility-button"
            iconFilled={revealConfirmPassword}
            slot="suffix"
            icon={revealConfirmPassword ? 'visibility' : 'visibility_off'}
            class="mr-[0.375rem] w-[1.75rem] h-[1.75rem] focus-visible:shadow-none focus-visible:bg-[--icon-button-variants-style-standard-surface-hover]"
            on:click={() => {
              revealConfirmPassword = !revealConfirmPassword;
            }}
          />
        </TextInput>
      </FormControl>
    </div>
  </div>

  <Button
    data-testid="submit-button"
    type="submit"
    size="lg"
    disabled={disableSubmit}
  >
    Sign Up
  </Button>
</Form>

<Switcher
  {companyPrimaryColor}
  text="Have an existing account or want to use social"
  buttonText="sign in instead?"
  toggleCardState={toggleSignIn}
/>
